
import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Building2, Users, BarChart3, Shield, Zap, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const Enterprise = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow pt-28 pb-16">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h1 className="text-4xl font-bold text-apple-text mb-6">
                Enterprise Recruitment Solutions
              </h1>
              <p className="text-xl text-apple-text-secondary mb-8">
                Scalable, customizable hiring solutions designed for large organizations with complex recruitment needs.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-2 rounded-full mr-4">
                    <Zap className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-apple-text">Streamlined Mass Hiring</h3>
                    <p className="text-apple-text-secondary">Process thousands of applications efficiently with AI-powered screening.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-purple-100 p-2 rounded-full mr-4">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-apple-text">Custom Assessment Design</h3>
                    <p className="text-apple-text-secondary">Create role-specific assessments tailored to your company's unique requirements.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-green-100 p-2 rounded-full mr-4">
                    <BarChart3 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-apple-text">Advanced Analytics</h3>
                    <p className="text-apple-text-secondary">Comprehensive hiring metrics and insights to optimize your recruitment process.</p>
                  </div>
                </div>
              </div>
              <div className="mt-10">
                <Link 
                  to="/contact" 
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-full hover:opacity-90 transition-all mr-4"
                >
                  Request Demo
                </Link>
                <Link 
                  to="/contact" 
                  className="px-8 py-3 border border-gray-300 text-apple-text font-medium rounded-full hover:bg-gray-50 transition-all"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&q=80&w=800" 
                alt="Enterprise Recruitment" 
                className="rounded-lg shadow-lg"
              />
            </div>
          </div>

          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-apple-text mb-4">
              Why Large Organizations Choose True Talent Apply
            </h2>
            <p className="text-xl text-apple-text-secondary max-w-3xl mx-auto">
              Trusted by leading companies across India for high-volume, high-quality hiring
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <Building2 className="h-12 w-12 text-apple-blue mb-6" />
              <h3 className="text-xl font-semibold text-apple-text mb-3">Scale with Confidence</h3>
              <p className="text-apple-text-secondary">
                Whether you're hiring dozens or thousands of employees, our platform scales to meet your needs with consistent quality and performance.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <Globe className="h-12 w-12 text-apple-blue mb-6" />
              <h3 className="text-xl font-semibold text-apple-text mb-3">Geographic Flexibility</h3>
              <p className="text-apple-text-secondary">
                Recruit across multiple locations in India with location-specific job postings, assessments, and talent pools.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <Shield className="h-12 w-12 text-apple-blue mb-6" />
              <h3 className="text-xl font-semibold text-apple-text mb-3">Enterprise-Grade Security</h3>
              <p className="text-apple-text-secondary">
                Enhanced security features, data protection, and compliance measures designed for enterprise requirements.
              </p>
            </div>
          </div>

          <div className="bg-apple-gray rounded-xl p-12 mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-apple-text mb-6">
                  Tailored Enterprise Solutions
                </h2>
                <p className="text-apple-text-secondary mb-6">
                  Our enterprise solutions can be customized to address your specific recruitment challenges:
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="bg-white rounded-full p-1 mr-3 mt-1">
                      <div className="bg-apple-blue rounded-full h-2 w-2"></div>
                    </div>
                    <p className="text-apple-text-secondary">White-labeled assessment platform with your branding</p>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-white rounded-full p-1 mr-3 mt-1">
                      <div className="bg-apple-blue rounded-full h-2 w-2"></div>
                    </div>
                    <p className="text-apple-text-secondary">Custom integration with your existing HRIS and ATS systems</p>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-white rounded-full p-1 mr-3 mt-1">
                      <div className="bg-apple-blue rounded-full h-2 w-2"></div>
                    </div>
                    <p className="text-apple-text-secondary">Specialized assessment content for industry-specific roles</p>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-white rounded-full p-1 mr-3 mt-1">
                      <div className="bg-apple-blue rounded-full h-2 w-2"></div>
                    </div>
                    <p className="text-apple-text-secondary">Dedicated account management and priority support</p>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-white rounded-full p-1 mr-3 mt-1">
                      <div className="bg-apple-blue rounded-full h-2 w-2"></div>
                    </div>
                    <p className="text-apple-text-secondary">Volume-based pricing with significant discounts</p>
                  </li>
                </ul>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800" 
                  alt="Enterprise Solutions" 
                  className="rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-3xl font-bold text-apple-text mb-4">
              Ready to Transform Your Enterprise Hiring?
            </h2>
            <p className="text-xl text-apple-text-secondary max-w-3xl mx-auto mb-8">
              Schedule a consultation with our enterprise team to discuss your specific needs.
            </p>
            <Link 
              to="/contact" 
              className="px-10 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-full hover:opacity-90 transition-all text-lg"
            >
              Book a Consultation
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Enterprise;
